import ServiceLocator from "../../../../../../ServiceLocator";
/**
 * Helper to fetch Generic Share daily streaks for a batch of players, so the
 * Activities feed can badge avatars with a flame.
 *
 * Why this goes through the game iframe rather than calling farm2 directly: an
 * unsigned cross-origin request to shareStreak.php gets framed by
 * IFrameController::needsFraming and comes back as the game's HTML page instead
 * of JSON. The game iframe holds the signed session, so it does the lookup and
 * hands the result back over Xing.
 *
 * Shaped after FeedClaimHelper: register rules once, ping-pong for reachability,
 * queue requests until the game answers, then match responses by transaction id.
 */
var Farm2StreakHelper = (function () {
    var _xingRegistered = false;    /* Have we registered our rules with Xing? */
    var _pingDone = false;          /* Did a ping test go through? */
    var _flushing = false;
    var _txnNumber = 0;
    var _txnRegister = {};          /* txn id -> { zids, onUpdate, timer } */
    var _queue = [];                /* batches waiting for the ping to land */
    var _cache = {};                /* zid -> { value, at } */
    var _inFlight = {};             /* zid -> true while a request is outstanding */
    var _pingTimer = null;          /* retry handle while waiting for the first pong */
    var _pingAttempts = 0;
    var _pendingZids = {};          /* zid -> true, accumulating during the debounce */
    var _pendingCallbacks = [];     /* subscribers waiting on the pending batch */
    var _debounceTimer = null;
    var _consecutiveFailures = 0;   /* drives the retry backoff */
    var _backoffUntil = 0;          /* no new requests before this timestamp */
    var _subscribers = [];          /* long-lived listeners for pushed updates */

    /* Internal constants */
    var _constants = {
        fv2IframeID: "farm2-game-frame",
        UWG_TYPE_EGRESS_STREAK_REQ: "UWG_STREAK_REQ",
        UWG_TYPE_INGRESS_STREAK_RESP: "UWG_STREAK_RESP",
        /**
         * Unsolicited push from the game when the player's own streak changes, so
         * their badge updates on the spot rather than after CACHE_TTL_MS.
         */
        UWG_TYPE_INGRESS_STREAK_UPDATE: "UWG_STREAK_UPDATE",
        /**
         * Distinct from FeedClaimHelper's "fv2pong". Xing keys registered rules by
         * name, so reusing that name would silently replace the feed-claim rule and
         * break claiming. Both rules see every UWG_PONG and act only on their own sig.
         */
        PONG_SIG: "fv2streakpong",
        /** The server caps a batch at 50; stay in step with it. */
        MAX_BATCH: 50,
        /**
         * How long a fetched streak is trusted. A streak changes at most once a day,
         * but the rail can stay open far longer than that, so re-check occasionally.
         */
        CACHE_TTL_MS: 5 * 60 * 1000,
        /**
         * The rail renders well before the game iframe calls activateXing(), so the
         * first pings land on a window with no message listener attached yet and are
         * simply dropped. Keep pinging until the game answers.
         */
        PING_RETRY_MS: 1500,
        MAX_PING_ATTEMPTS: 20,
        /**
         * If a sent batch is never answered, release its zids so a later render can
         * retry them. Without this they stay marked in-flight forever and those
         * avatars never get a badge.
         */
        REQUEST_TIMEOUT_MS: 15000,
        /**
         * Window over which incoming zids are collected into one batch. The feed
         * trickles in a item at a time, so without this each render produced its own
         * single-zid request.
         */
        DEBOUNCE_MS: 300,
        /**
         * Backoff after a failure, doubling per consecutive failure. A transient farm2
         * error must not turn into a request storm, but it also must not be cached as
         * "no streak" — so the zids are released and simply retried later.
         */
        BACKOFF_BASE_MS: 2000,
        BACKOFF_MAX_MS: 60000
    };

    /* Rules compliant with Xing for streaks */
    var _streakRules = [
        {
            activated: false,
            name: "fv2streakpong",
            passes: [
                ServiceLocator.Xing.XingChecker.Validate,
                ServiceLocator.Xing.XingChecker.IsPong
            ],
            handler: (d) => {
                // Every pong reaches every pong rule, so only act on our own signature.
                if (d !== undefined && d.sig === _constants.PONG_SIG) {
                    _pingDone = true;
                    _stopPinging();
                    _flush();
                }
            }
        },
        {
            activated: false,
            name: "UWG_STREAK_RESP",
            passes: [
                ServiceLocator.Xing.XingChecker.Validate,
                (d) => { return d.type === _constants.UWG_TYPE_INGRESS_STREAK_RESP; }
            ],
            handler: (resp) => { _onStreakResponse(resp); }
        },
        {
            activated: false,
            name: "UWG_STREAK_UPDATE",
            passes: [
                ServiceLocator.Xing.XingChecker.Validate,
                (d) => { return d.type === _constants.UWG_TYPE_INGRESS_STREAK_UPDATE; }
            ],
            handler: (update) => { _onStreakPush(update); }
        }
    ];

    /** Send one reachability ping to the game. */
    function _ping() {
        ServiceLocator.Xing.Send(
            _constants.fv2IframeID,
            ServiceLocator.Xing.XingConstants.UWG_TYPE_EGRESS_PING,
            {
                "sig": _constants.PONG_SIG,
                "msg": "FV2 streak ping-pong test"
            }
        );
    }

    /**
     * Keep pinging until the game answers.
     *
     * A single ping is not enough: the rail mounts and requests streaks before the
     * game iframe has run activateXing(), so early pings hit a window that is not
     * listening yet and vanish. Relying on the next Fetch() to re-ping does not work
     * either, because once every zid is in-flight Fetch returns early and never pings
     * again — which deadlocks the queue permanently.
     */
    function _startPinging() {
        if (_pingDone) {
            return;
        }
        if (_pingTimer !== null) {
            return; // already retrying
        }

        _pingAttempts = 1;
        _ping();

        _pingTimer = setInterval(() => {
            if (_pingDone) {
                _stopPinging();
                return;
            }
            if (_pingAttempts >= _constants.MAX_PING_ATTEMPTS) {
                _stopPinging();
                // Release the queue, otherwise those zids stay in-flight forever and
                // nothing ever restarts the ping — a permanent stall.
                _abandonQueued();
                _noteFailure();
                return;
            }
            _pingAttempts++;
            _ping();
        }, _constants.PING_RETRY_MS);
    }

    function _stopPinging() {
        if (_pingTimer !== null) {
            clearInterval(_pingTimer);
            _pingTimer = null;
        }
    }

    /**
     * Record a failure and hold off new requests for a while.
     *
     * Deliberately does NOT write anything to _cache. Caching a zero on failure would
     * hide a real badge for a full TTL, which is far worse than showing it late.
     */
    function _noteFailure() {
        _consecutiveFailures++;
        const wait = Math.min(
            _constants.BACKOFF_BASE_MS * Math.pow(2, _consecutiveFailures - 1),
            _constants.BACKOFF_MAX_MS
        );
        _backoffUntil = Date.now() + wait;
    }

    function _noteSuccess() {
        _consecutiveFailures = 0;
        _backoffUntil = 0;
    }

    /**
     * Give up on everything currently queued or accumulating, releasing the zids so a
     * later render can ask again.
     *
     * This is what stops a permanent stall. Batches that were queued but never sent
     * had no timeout armed — the timeout is armed at send time — so without releasing
     * them here their zids stay marked in-flight forever. Every later Fetch would then
     * see "nothing new", return early, and never reach the code that restarts pinging.
     */
    function _abandonQueued() {
        _queue.forEach((batch) => {
            const entry = _txnRegister[batch.transactionId];
            if (entry) {
                if (entry.timer) {
                    clearTimeout(entry.timer);
                }
                entry.zids.forEach((zid) => {
                    delete _inFlight[zid];
                });
                delete _txnRegister[batch.transactionId];
            }
        });
        _queue = [];

        // Anything still sitting in the debounce buffer as well.
        Object.keys(_pendingZids).forEach((zid) => {
            delete _inFlight[zid];
        });
        _pendingZids = {};
        _pendingCallbacks = [];
        if (_debounceTimer !== null) {
            clearTimeout(_debounceTimer);
            _debounceTimer = null;
        }
    }

    /**
     * A sent batch went unanswered. Release its zids so they can be retried rather
     * than staying stuck in-flight for the life of the page.
     * @param {string} txnId
     */
    function _timeoutTxn(txnId) {
        const entry = _txnRegister[txnId];
        if (!entry) {
            return;
        }
        delete _txnRegister[txnId];
        entry.zids.forEach((zid) => { delete _inFlight[zid]; });
    }

    /** Normalise whatever the server sent into a predictable shape. */
    function _normalise(raw) {
        if (!raw) {
            return { currentStreak: 0, flameTier: 0, graceActive: false };
        }
        return {
            currentStreak: raw.currentStreak ? parseInt(raw.currentStreak, 10) : 0,
            flameTier: raw.flameTier ? parseInt(raw.flameTier, 10) : 0,
            graceActive: raw.graceActive === true
        };
    }

    /** Response from the game: { transactionId, streaks } or { transactionId, error } */
    function _onStreakResponse(resp) {
        var txnId = (resp && resp.transactionId) ? resp.transactionId : null;
        var entry = txnId ? _txnRegister[txnId] : null;

        if (!entry) {
            return;
        }
        if (entry.timer) {
            clearTimeout(entry.timer);
        }
        delete _txnRegister[txnId];

        // A failed lookup says nothing about these players' streaks. Release them and
        // retry later rather than caching zeros — caching would hide real badges for a
        // full TTL because of a transient farm2 error.
        if (resp && resp.error) {
            entry.zids.forEach(function (zid) { delete _inFlight[zid]; });
            _noteFailure();
            return;
        }

        var streaks = (resp && resp.streaks) ? resp.streaks : {};
        var now = Date.now();
        var fresh = {};

        entry.zids.forEach(function (zid) {
            // On a SUCCESSFUL response, a player the server did not mention genuinely
            // has no visible streak, so caching a zero is correct and stops them being
            // re-requested every render. Note Farm2Blobs::multiGet reads memcache only,
            // so an uncached player legitimately comes back absent.
            var value = _normalise(streaks[zid]);
            _cache[zid] = { value: value, at: now };
            fresh[zid] = value;
            delete _inFlight[zid];
        });

        _noteSuccess();

        // Goes to this batch's callbacks AND every long-lived subscriber, so a
        // component whose own request was coalesced or abandoned still gets the value.
        _deliver(fresh, entry.callbacks);
    }

    /**
     * The game telling us a player's streak just changed — in practice the signed-in
     * player, right after they shared.
     * @param {object} update { zid, currentStreak, flameTier, graceActive }
     */
    function _onStreakPush(update) {
        if (!update || !update.zid) {
            return;
        }

        const zid = "" + update.zid;
        const value = _normalise(update);

        _cache[zid] = { value: value, at: Date.now() };
        // A push is authoritative, so anything outstanding for this player is moot.
        delete _inFlight[zid];

        const map = {};
        map[zid] = value;

        _deliver(map, null);
    }

    /**
     * Hand a zid -> streak map to everyone who should hear about it: the callbacks
     * that came with this particular batch, plus every long-lived subscriber.
     *
     * Both audiences matter. A batch callback belongs to whoever happened to trigger
     * the request, but a component that subscribed and whose own request was
     * abandoned or coalesced away would otherwise never be told the answer — which is
     * exactly how the nav badge ended up permanently blank while the feed updated.
     *
     * Deduplicated by function identity, since components pass the same bound method
     * to both Fetch and Subscribe.
     *
     * @param {object} map zid -> streak
     * @param {function[]} batchCallbacks callbacks recorded for this batch, may be null
     */
    function _deliver(map, batchCallbacks) {
        const audience = (batchCallbacks || []).concat(_subscribers);
        const called = [];

        audience.forEach(function (fn) {
            if (typeof fn !== "function" || called.indexOf(fn) !== -1) {
                return;
            }
            called.push(fn);
            try {
                fn(map);
            } catch (e) {
                // One broken listener must not stop the rest being told.
            }
        });

        return called.length;
    }

    /** Register the Xing rules once. Needed before any push can be received. */
    function _ensureRulesRegistered() {
        if (_xingRegistered) {
            return;
        }
        ServiceLocator.Xing.RegisterRules(_streakRules);
        ServiceLocator.Xing.ActivateRules(_streakRules);
        _xingRegistered = true;
    }

    function _getNewTxnId() {
        return "" + (++_txnNumber);
    }

    /**
     * Request streaks for a list of player zids.
     *
     * Only asks for zids that are neither freshly cached nor already outstanding, so
     * this is safe to call on every render. The callback fires only when new data
     * arrives, never for values the caller already has.
     *
     * Requests are coalesced over a short window before being sent. The feed arrives
     * by trickle — one item per render — so without this a page load produced a
     * separate single-zid transaction per item, and both feed and suggested-friends
     * components adding zids independently made it worse.
     *
     * @param {string[]} zids
     * @param {function} onUpdate receives a map of zid -> { currentStreak, flameTier, graceActive }
     */
    function Fetch(zids, onUpdate) {
        if (!zids || zids.length === 0) {
            return;
        }

        const now = Date.now();
        const seen = {};
        const known = {};
        let added = 0;

        const backingOff = _backoffUntil > now;

        zids.forEach(function (rawZid) {
            if (!rawZid) {
                return;
            }
            const zid = "" + rawZid;
            if (seen[zid]) {
                return;
            }
            seen[zid] = true;

            // Surface anything we already know straight away, even if it is stale and
            // about to be refreshed. A component that has just mounted holds no state,
            // so without this its badges stay blank until the cache expires — and a
            // second component asking for a zid the first already fetched would never
            // be told the answer at all.
            const cached = _cache[zid];
            if (cached) {
                known[zid] = cached.value;
            }

            if (_inFlight[zid]) {
                return;
            }
            if (cached && (now - cached.at) < _constants.CACHE_TTL_MS) {
                return;
            }
            if (backingOff) {
                return;
            }

            // Marked in-flight at accumulate time, not send time, so repeated renders
            // during the debounce window do not add the same zid twice.
            _inFlight[zid] = true;
            _pendingZids[zid] = true;
            added++;
        });

        // Hand back the known values before doing any network work. Safe to call
        // synchronously: the subscriber compares against its own state and skips the
        // update when nothing changed, so this cannot loop.
        if (Object.keys(known).length > 0 && typeof onUpdate === "function") {
            try {
                onUpdate(known);
            } catch (e) {
                // A broken subscriber must not block the network fetch below.
            }
        }

        if (typeof onUpdate === "function" && _pendingCallbacks.indexOf(onUpdate) === -1) {
            _pendingCallbacks.push(onUpdate);
        }

        if (added === 0) {
            return;
        }

        if (_debounceTimer !== null) {
            clearTimeout(_debounceTimer);
        }
        _debounceTimer = setTimeout(_sendPending, _constants.DEBOUNCE_MS);
    }

    /** Turn everything accumulated during the debounce window into one batch. */
    function _sendPending() {
        _debounceTimer = null;

        let wanted = Object.keys(_pendingZids);
        const callbacks = _pendingCallbacks.slice();
        _pendingZids = {};
        _pendingCallbacks = [];

        if (wanted.length === 0) {
            return;
        }

        if (wanted.length > _constants.MAX_BATCH) {
            // Anything over the cap goes back to being unknown so a later render picks
            // it up, rather than being silently forgotten.
            const overflow = wanted.slice(_constants.MAX_BATCH);
            overflow.forEach(function (zid) { delete _inFlight[zid]; });
            wanted = wanted.slice(0, _constants.MAX_BATCH);
        }

        const txnId = _getNewTxnId();
        // Kept as a plain array rather than a wrapper function so _deliver can
        // dedupe it against the long-lived subscriber list.
        _txnRegister[txnId] = { zids: wanted, callbacks: callbacks };
        _queue.push({ transactionId: txnId, zids: wanted });

        _ensureRulesRegistered();

        // Reachability check before anything is sent. Starts a retry loop rather
        // than firing once, so a ping lost during game startup recovers.
        _startPinging();

        _flush();
    }

    function _flush() {
        if (!_pingDone || _flushing) {
            return;
        }
        _flushing = true;
        while (_queue.length > 0) {
            const batch = _queue.shift();

            // Arm the timeout only now that it is actually on the wire. A batch still
            // sitting in the queue is waiting on the pong, not on the game.
            const entry = _txnRegister[batch.transactionId];
            if (entry && !entry.timer) {
                entry.timer = setTimeout(
                    () => { _timeoutTxn(batch.transactionId); },
                    _constants.REQUEST_TIMEOUT_MS
                );
            }

            ServiceLocator.Xing.Send(
                _constants.fv2IframeID,
                _constants.UWG_TYPE_EGRESS_STREAK_REQ,
                batch
            );
        }
        _flushing = false;
    }

    /**
     * Read a cached streak without triggering a request.
     * @param {string} zid
     * @return {object|null}
     */
    function Peek(zid) {
        var cached = _cache["" + zid];
        return cached ? cached.value : null;
    }

    /**
     * Drop a cached entry so the next Fetch re-reads it. Useful after the player
     * shares, since their own badge is then stale.
     * @param {string} zid
     */
    function Invalidate(zid) {
        delete _cache["" + zid];
    }

    /**
     * Listen for streak changes pushed by the game, independently of Fetch.
     *
     * Fetch's callback only covers the batch it was called for, so it cannot deliver
     * an unsolicited update. Components subscribe once on mount and unsubscribe on
     * unmount using the returned function.
     *
     * @param {function} onUpdate receives a map of zid -> streak
     * @return {function} call to unsubscribe
     */
    function Subscribe(onUpdate) {
        if (typeof onUpdate !== "function") {
            return function () {};
        }
        if (_subscribers.indexOf(onUpdate) === -1) {
            _subscribers.push(onUpdate);
        }
        // Register now, otherwise a push arriving before the first Fetch is dropped.
        _ensureRulesRegistered();

        return function () {
            const i = _subscribers.indexOf(onUpdate);
            if (i !== -1) {
                _subscribers.splice(i, 1);
            }
        };
    }

    return {
        Fetch: Fetch,
        Subscribe: Subscribe,
        Peek: Peek,
        Invalidate: Invalidate
    };
})();

export default Farm2StreakHelper;
